import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { DatabaseWrapper, DatabaseWrapperProvider, DocWatcher } from 'services/database-wrapper';
import { AuthService } from 'services/auth.service';
import { OnlineStateService } from 'services/online-state.service';
import {
  Food,
  FoodHeader,
  INFOODS,
  foodDatabaseName,
  FoodStatus,
  FoodMeasure,
  ComplexFood,
  Nutrients,
  NutrientTargets,
  generateUserFoodId,
  getIngredientText,
  isLogMealById,
  isLogMeal,
  AnyFood,
  isComplexFoodById,
  isLegacyMealId,
  normalizeFoodFormat,
} from '@cf-shared/data-model/food';
import { UserDb } from 'services/user-db.service';
import { LogRecordType } from '@cf-shared/data-model/user-log';
import { DateTime } from 'luxon';
import { sortBy, unionBy } from 'lodash-es';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { Recipe, isRecipe, isRecipeById } from '@cf-shared/data-model/recipe';
import { buildSplitIngredients, describeRecordedWeight, getComplexFoodTotalWeight, scaleSplitIngredientsToPortion, SplitPortionOutcome } from '@cf-shared/data-model/split-recipe-portion';
import { FirebaseService } from 'services/firebase.service';
import { DeferredPromise, generateAllGTINCombinations, sleep, timeout } from '@cf-shared/shared-util';
import { DebugService } from 'services/debug.service';
import { CloudFunctionsService } from './cloud-functions.service';
import { MealTunerIngredient, MealTunerMeal, MealTunerOutcome, MealTunerParams } from '@cf-shared/meal-tuner-interface';
import { MACRO_INGREDIENT_FOOD_ID, MealTuner } from '@cf-shared/meal-tuner';
import { rebuildMealIngredientsFromTuner } from './meal-tuner-rebuild';
import { mealRowsTunedWithSplitParts } from './split-tuning-rows';
import { deepClone, deepOverwrite } from '@cf-shared/deep-helpers';
import * as crypto from 'crypto';
import { LoggingService } from './logging.service';
import { getApiOriginAsync } from 'util/backend-origin';
import { markCallerReportsFailure } from 'app/fetch-failure-classifier';
import {
  FOOD_READ_NETWORK_ERROR_PROBLEM, FOOD_READ_NETWORK_ERROR_TRANSIENT, FoodReadConnectionMoment, FoodReadFailedError,
  FoodReadFailurePolicy, FoodReadFailureReport, FoodReadOutcome, chooseFoodReadFailureReport, classifyFoodReadError,
} from './food-read-error';
import { publishMissingRecipeIngredientsAsync, repairSharedRecipesAsync, reportOncePerDevice } from './recipe-ingredient-publish';
import { isNetworkError } from './cloud-functions-errors';
import { SearchConnectionError, describeSearchFailureForReport } from './food-search-error';
import { AUTH_SETTLED_WAIT_MS, ElasticSearchDeps, SEARCH_REQUEST_TIMEOUT_MS, SEARCH_STALL_CONNECTION_CHECK_MS, searchFoodHeadersByEANsAsync, searchFoodHeadersByNameAsync } from './food-search-elastic';
import { TypoTolerance } from '@cf-shared/food-search/food-search-query';
import { requestFoodSuggestionsAsync } from './food-suggest-client';
import { SuggestionRow } from '@cf-shared/food-search/food-suggestion-row';
import { buildFoodHeader } from './food-header-builder';
import { describeRowWithoutId, splitRowsByUsableId } from './food-search-rows-without-id';
import { AUTO_PLAN_FOODS_CACHED_KEY, AUTO_PLAN_FOODS_RECIPE_PREFIX, AutoPlanFoodsCache } from './food-auto-plan-cache';
import { DEFAULT_FOODS_FALLBACK_WAIT_MS, DEFAULT_FOODS_VIEW_WAIT_MS, loadDefaultFoodsAsync } from './food-search-default-results';
import { dropRepeatedRecentMeals } from './recent-meals-dedupe';
import { MEAL_DESCRIPTION_SEPARATOR, recentMealSearchNames } from './recent-meal-search-names';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { isoTimeToHours } from '@cf-shared/time';
import { MealNameSuggestion, pickMealNameSuggestion } from 'services/meal-name-suggestion';
import { FormatNutrients, formatRelativeDay, formatScheduledMealName } from 'util/formatting';
import { Ingredient, isIngredientReal, IngredientType, isIngredientMacro, IngredientReal, ingredientFoodSetsMatch, macroIngredientNutrientsForWeight, newUnusableNutrientInput } from '@cf-shared/data-model/ingredient';
import { reportUnusableNutrientInput } from 'util/nutrient-data-quality-reporter';
import { sumSavedMealNutrients } from './meal-nutrient-total';
import { CannotTuneReason, classifyCannotTune } from 'util/cannot-tune-reason';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { untranslatedString } from 'models/translated-string';
import { isDatabaseTornDown, isDatabaseUnavailable } from 'services/database-unavailable-error';
import { errorReportFields, isOfflineOrTransientNetworkError } from 'services/error-patterns';
import { isBackgroundReadNotReady } from 'services/background-read-failure';
import { declareEvent, PubSubService } from 'services/pubsub.service';
import { StapleDbService } from 'services/staple-db.service';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { isStapleId, preferredStapleRead } from 'services/staple-food-read';
import {
  FOOD_FRESHNESS_PAUSE_AFTER_FAILURE_MS,
  FOOD_FRESHNESS_READ_LIMIT_MS,
  FoodFreshnessLog,
  FRESHNESS_READ_GAVE_UP,
  isFoodWorthCheckingForFreshness,
  readWhenReadyWithinDeadlineAsync,
  serverCopyWouldChangeCachedCopy,
} from 'services/food-freshness';
interface TrigramQueryResult {
  id: string;
  key: string;
  value: number;
}

interface TrigramCacheEntry {
  foods: TrigramQueryResult[];
  age: number;
}

export type MealTunerSource = {
  ingredients?: Ingredient[];
  _id?: string;
}

export type { MealNameSuggestion };

export interface MealCandidatesOptions {
  searchOnlyLocal?: boolean;
  includeRecentMeals?: boolean;
  recentMealsDays?: number;
}

// re-export so existing callers can keep importing it from this service
export { FoodReadFailedError } from './food-read-error';

// How long a page waits for the shared food databases before giving up and telling the user
// the page could not be opened. Only ever spent when a page is entered before those databases
// have started — a page opened from a link during a cold boot (#4681). A warm resume starts the
// food database in Phase A, so a restored page (df-5d2fd4601fb1) finds it ready (#6790). It is
// also the bound on a shared-food read waiting for the server connection (awaitGlobalFoodDbAsync).
// In the running app the databases are long since ready and both waits return at once.
//
// It MUST stay below NAV_RESTORE_WATCHDOG_MS (20 000 ms, nav-restore-coordination.ts), because
// this wait happens inside the stretch of boot that watchdog covers. A page guard that waited
// longer than 20 000 ms would make the watchdog report the boot as stuck when it is simply
// waiting — a false alarm in exactly the telemetry this change exists to make trustworthy.
// The gap left below it is what a slow phone gets to finish opening the databases in.
//
// It lives here, beside the wait it is the budget for, because more than one page needs it and
// a second copy of the number would be free to drift away from that 20 000 ms ceiling.
export const FOOD_DB_READY_TIMEOUT_MS = 15000;

/**
 * A food's liked / disliked vote changed. Lists that show a like mark copy `liked` when they
 * search, so they listen for this to update the mark without searching again.
 */
export const FoodLikedChangedEvent = declareEvent<{ foodId: string; liked: -1 | 1 | undefined }>('food:likedChanged');

@Injectable()
export class FoodDbService {
  database: DatabaseWrapper;

  // Resolves once startAsync has wired up `database`. Before that every read here throws
  // a TypeError on `this.database`. A cold start begins the shared food databases in a LATER
  // boot phase than navigation; a warm restart starts this one in Phase A, before navigation is
  // restored, and connects it to the server only after the login (#6790).
  //
  // That ordering is why #4681 happened: opening the app straight from a shared food link runs
  // the food page while this service is still empty, so the read blew up, the page was left
  // showing its blank placeholder, and nothing was reported. Callers that can run that early
  // wait on this first — see awaitReadyWithTimeoutAsync.
  private whenInitialized = DeferredPromise.defer<boolean>();
  private initializedResolved = false;
  // the wrapper whose start finished, so startAsync never reuses one whose start threw
  private startedDatabase: DatabaseWrapper | null = null;
  // the wrapper whose wait for a server connection already ran out — see awaitGlobalFoodDbAsync
  private globalFoodDbWaitGaveUp: DatabaseWrapper | null = null;
  // the wait currently running for that connection, shared by the reads that arrive meanwhile
  private globalFoodDbWait: { database: DatabaseWrapper; done: Promise<void> } | null = null;
  trigramCache: Map<string, TrigramCacheEntry> = new Map<string, TrigramCacheEntry>();
  trigramCacheAge = 0;
  foodHeaderCache = new Map<string, FoodHeader>();

  /**
   * Copies the official recipe library into the user's own database, and holds the
   * meal-planning pages back until that has happened — the meal planner throws on a missing
   * ingredient, so a fresh paid signup used to see empty and nonsense rows (#3244, #3245).
   * The state machine itself, and why a failed attempt leaves the gate unresolved on purpose,
   * are in food-auto-plan-cache.ts. Built in the constructor, because everything it borrows
   * from this service is read lazily through the closures below.
   */
  private autoPlanFoodsCache: AutoPlanFoodsCache;

  /** Resolves true once the recipe library is in the user's database. */
  public get whenAutoPlanFoodsCached(): DeferredPromise<boolean> {
    return this.autoPlanFoodsCache.whenCached;
  }

  /**
   * Waits for that copying, giving up after `timeoutMs` and answering false. See
   * food-auto-plan-cache.ts.
   */
  async awaitAutoPlanFoodsCachedWithTimeoutAsync(timeoutMs: number): Promise<boolean> {
    return this.autoPlanFoodsCache.awaitCachedWithTimeoutAsync(timeoutMs);
  }

  /**
   * Which foods have already been asked about since the app started, so each is asked once.
   * In memory on purpose: the next app start checks again, because nothing tells the app when a
   * correction lands on the server. See `services/food-freshness.ts`.
   */
  private readonly freshnessLog = new FoodFreshnessLog();

  constructor(
    private auth: AuthService,
    private userDb: UserDb,
    private debug: DebugService,
    private cloudFunctionsService: CloudFunctionsService,
    private dbProvider: DatabaseWrapperProvider,
    public logging: LoggingService,
    private firebase: FirebaseService,
    private pubSub: PubSubService,
    private stapleDb: StapleDbService,
    private onlineState: OnlineStateService,
  ) {
    this.autoPlanFoodsCache = new AutoPlanFoodsCache({
      readCachedFlag: () => !!localStorage.getItem(AUTO_PLAN_FOODS_CACHED_KEY),
      setCachedFlag: () => localStorage.setItem(AUTO_PLAN_FOODS_CACHED_KEY, '1'),
      clearCachedFlag: () => localStorage.removeItem(AUTO_PLAN_FOODS_CACHED_KEY),
      hasOfficialRecipesCachedAsync: () => this.hasOfficialRecipesCachedAsync(),
      readCandidatesAsync: () => this.readAutoPlanCandidatesAsync(),
      fetchAndCacheFoodAsync: foodId => this.getFoodByIdAsync(foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }),
      listCachedOfficialRecipeIdsAsync: () => this.userDb.database.getIdsByIdHeadAsync(AUTO_PLAN_FOODS_RECIPE_PREFIX),
      // waits for the shared database to be started and connected first; the caller bounds the
      // whole wait
      listSharedOfficialRecipeIdsAsync: async () => {
        await this.whenInitialized;
        await this.awaitGlobalFoodDbAsync();
        return this.database.getIdsByIdHeadAsync(AUTO_PLAN_FOODS_RECIPE_PREFIX);
      },
      readSharedFoodAsync: async foodId => await this.getFoodByIdAsync(foodId, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }) as ComplexFood,
      isDatabaseAvailable: () => !!this.database,
      reportCachingFailure: err => {
        // if offline, or the database is closing under us (sign-out), this is routine: the
        // next gated visit retries. anything else would keep failing with no trace (#5191)
        if (isOfflineOrTransientNetworkError(err) || isDatabaseUnavailable(err)) return;
        this.logging.trackSilentProblem('AutoPlanFoodsCachingFailed', errorReportFields(err));
      },
    });
  }

  /** Start the food database and connect it to the server. The cold start's one call. */
  async initializeAsync() {
    await this.startAsync();
    await this.connectRemoteAsync();
  }

  /**
   * Set the food database up without touching the network. It is remote-only, so before the
   * CouchDB login there is no server handle to open and this does nothing else: a warm restart
   * calls it in Phase A, so the pages that wait on `whenInitialized` can open before the login
   * (#6790). Reads of a shared food then wait for `connectRemoteAsync` — see awaitGlobalFoodDbAsync.
   */
  async startAsync() {
    // if Phase A already started it, a second start would leave the first wrapper subscribed.
    // only a wrapper that FINISHED starting counts: one whose start threw (a sign-out landing
    // mid-start leaves auth.user null) is replaced rather than reused, or whenInitialized would
    // never resolve and every page guard waiting on it would time out
    if (this.startedDatabase && this.startedDatabase === this.database && !this.database.isShuttingDown) return;

    this.database = this.dbProvider.createDatabaseWrapper();
    this.database.setRemoteOnly();

    await this.database.initAsSharedDatabaseAsync(this.auth.user, foodDatabaseName);
    this.startedDatabase = this.database;

    // announce readiness only now — `database` is assigned above, but a reader that arrived
    // between the two lines would still race the shared-database handshake
    if (!this.initializedResolved) {
      this.initializedResolved = true;
      this.whenInitialized.resolve(true);
    }
  }

  /**
   * Connect the food database to the server, once the CouchDB login has set the session. Does
   * nothing when it is connected already (the wrapper connects itself when it starts after the
   * login, or when the app comes back online).
   */
  async connectRemoteAsync() {
    await this.database.connectRemoteOnlyAsync();
  }

  /**
   * Wait until the food database can read from the server, giving up after
   * FOOD_DB_READY_TIMEOUT_MS. On a warm restart the database is started before the login and
   * connected after it, so a read made in between would otherwise fail with "Database not
   * available" during an ordinary slow login (#6790). When the wait runs out — a login that
   * failed — the read goes ahead and fails as it always has, so nobody is held forever.
   *
   * The wait is spent only while a connection can still arrive. Offline, nothing will connect
   * the database until the app comes back online, so the read goes ahead at once. "Offline" is
   * read only after the app's first reachability check has answered: until then the online state
   * says Offline as a placeholder, and early in a warm restart that is exactly the slow-login
   * window this wait is for. That check shares the one FOOD_DB_READY_TIMEOUT_MS budget. And once
   * the wait has run out for this database it is not spent again: in a session whose login
   * failed, every later read would otherwise stall 15 s before failing exactly as it did before.
   *
   * Reads that arrive while a wait is running share it. During a slow login the first check is
   * itself held behind the login, and its gate files a report when it outlasts its bound, so one
   * wait per read would file one report per read.
   */
  private async awaitGlobalFoodDbAsync(): Promise<void> {
    const database = this.database;
    // if the wait already ran out for this database, there is nothing more to wait for
    if (this.globalFoodDbWaitGaveUp === database) return;
    // if no wait is running for this database, start one; otherwise join it
    if (this.globalFoodDbWait?.database !== database) {
      this.globalFoodDbWait = { database, done: this.waitForGlobalFoodDbAsync(database) };
    }
    const wait = this.globalFoodDbWait;
    try {
      await wait.done;
    } finally {
      // the next read decides afresh — offline may have become online since
      if (this.globalFoodDbWait === wait) this.globalFoodDbWait = null;
    }
  }

  /** The one wait awaitGlobalFoodDbAsync shares between the reads that arrive while it runs. */
  private async waitForGlobalFoodDbAsync(database: DatabaseWrapper): Promise<void> {
    const startedAt = performance.now();
    // the online state is a placeholder until the first check answers, so let it answer first
    await this.onlineState.awaitFirstProbeAsync(FOOD_DB_READY_TIMEOUT_MS);
    const elapsedMs = performance.now() - startedAt;
    // if nothing can connect the database right now
    if (!this.onlineState.isLikelyOnline()) {
      // a first check that used the whole budget without an answer is a wait that ran out
      if (elapsedMs >= FOOD_DB_READY_TIMEOUT_MS) this.globalFoodDbWaitGaveUp = database;
      return;
    }
    // at least 1 ms: timeout(0, …) means "never time out", which would hold the read forever
    const remainingMs = Math.max(1, FOOD_DB_READY_TIMEOUT_MS - elapsedMs);
    try {
      await timeout(remainingMs, database.whenGlobalFoodDbAvailable);
    } catch (e) {
      if (e?.name === 'TimeoutError') {
        this.globalFoodDbWaitGaveUp = database;
        return;
      }
      throw e;
    }
  }

  /**
   * Wait until this service can actually read, giving up after `timeoutMs`. Returns true when
   * it is ready, false when the wait ran out.
   *
   * For callers that can run before the shared food databases have started — chiefly a page
   * opened straight from a link, which happens during boot. Without this wait such a page
   * cannot tell "this food does not exist" from "I was asked too early", and #4681 is what
   * that looks like to a user: a shared recipe link that opens an empty page.
   *
   * Bounded rather than open-ended on purpose. Some flows never initialize this service at all
   * (an unfinished sign-in, a shutdown mid-boot), and a page that waited forever would hang
   * with a spinner instead of failing in a way the user can escape.
   */
  async awaitReadyWithTimeoutAsync(timeoutMs: number): Promise<boolean> {
    // Deliberately NOT `if (this.database)`. That field is assigned at the top of
    // initializeAsync, before the shared-database handshake it then awaits — so testing it
    // would report "ready" during the very window this wait exists to cover, and the caller
    // would go on to read a database that is still opening.
    if (this.initializedResolved) {
      return true;
    }
    // timeout() cancels its timer when the inner promise settles first, so this leaks nothing.
    // Narrowed to TimeoutError so a future change that rejects the deferred is not silently
    // reported as "we waited too long".
    try {
      await timeout(timeoutMs, this.whenInitialized);
      return true;
    } catch (e) {
      if (e?.name === 'TimeoutError') return false;
      throw e;
    }
  }

  async shutdownAsync() {
    if (this.database) {
      await this.database.shutdownAsync();
    }
  }

  /**
   * Publish the user's foods that an earlier session could not publish. Started with `void` at
   * every app start (and awaited by the admin page), so it never rejects: if the data is not
   * reachable right now it stops quietly and the next app start retries; any other failure is
   * reported (df-b99ce9dcabce).
   */
  async tryPublishUnpublishedFoodsAsync(adminMode: boolean = false) {
    try {
      await this.publishUnpublishedFoodsAsync(adminMode);
    } catch (err) {
      if (isBackgroundReadNotReady(err)) {
        console.log('Syncing unpublished foods: data not reachable right now, will retry next app start:', err);
        return;
      }
      this.logging.trackSilentProblem('UnpublishedFoodsSyncFailed', errorReportFields(err));
    }
  }

  private async publishUnpublishedFoodsAsync(adminMode: boolean) {
    await this.database.whenGlobalFoodDbAvailable;

    console.log('Syncing unpublished foods');
    const foodsRecipes = await this.getUserFoodsAsync();
    if (foodsRecipes) {
      // repair recipes shared before #3255, whose later-added or nested ingredients never
      // reached the shared database and show as blank rows to everybody who opens them.
      // this runs BEFORE the publishes scheduled below, so the two never publish the same
      // food at the same moment (both would write it as new, and one write would be refused)
      const sharedRecipes = foodsRecipes.filter(food => isRecipe(food) && food.local?.shared) as Recipe[];
      await this.repairSharedRecipesAsync(sharedRecipes);

      /* Filters foods from recipes, then,
       * filters foods with existing hashSource that says they are apparently unpublished
       */
      // filter out legacy meals and unshared recipes - they should not be published
      const foods = foodsRecipes.filter(food =>
        !isLegacyMealId(food._id) && !(isRecipe(food) && !food.local?.shared)
      );
      let unpublishedFoods = foods.filter(food => food.local && food.local.hashSource);
      const unpublishedFoodsCount = unpublishedFoods.length;
      console.log(`${unpublishedFoodsCount} unpublished food found`);

      if (unpublishedFoodsCount > 0) {
        let unpublishedFoodIds = unpublishedFoods.map(food => food._id);

        console.log('Querying global db for unpublished food matches');
        const alreadyPublished = await this.getFoodHeadersByIdsAsync(unpublishedFoodIds);
        const alreadyPublishedIds = alreadyPublished.map(header => header._id);

        // Filter out foods that we need to publish
        // Remove hashSource for these foods
        let alreadyPublishedFoods = unpublishedFoods.filter(food => alreadyPublishedIds.includes(food._id));
        alreadyPublishedFoods.forEach(food => {
          console.log(`Food is already published: ${food._id}`);
          delete food.local.hashSource;
          // nobody awaits this save, so its failure is handled here (df-523496a53870)
          void this.saveUserFoodAsync(food, 'cached')
            .catch(err => this.reportUnpublishedFoodFailure('UnpublishedFoodCacheSaveFailed', food._id, err));
        });

        // Filter out foods that are already published
        unpublishedFoods = unpublishedFoods.filter(food => !alreadyPublishedIds.includes(food._id));
        console.log(`${unpublishedFoods.length} new food to publish`);

        // filter out meals as we don't want to publish them
        unpublishedFoods.forEach((food, index) => {
          setTimeout(() => void this.publishOneUnpublishedFoodAsync(food, adminMode), index * 5000);
        });
      }

    }
  }

  /**
   * Publish one food that an earlier session could not publish, then clear its unpublished mark.
   * Runs from a timer, after `tryPublishUnpublishedFoodsAsync` has already returned, so its catch
   * no longer covers this and nothing else owns the promise: it never rejects (df-523496a53870).
   * The food keeps its `hashSource` mark on any failure, so the next app start tries again.
   */
  private async publishOneUnpublishedFoodAsync(food: Food, adminMode: boolean) {
    try {
      console.log(`Attempting to publish food: ${food._id}`);
      const result = await this.cloudFunctionsService.publishFoodAsync({ food, adminMode: adminMode });
      if (result.error) {
        console.log(`Error publishing food: ${food._id}`);
        console.log(result.error);
        return;
      }

      console.log(`Food successfully published: ${food._id}`);
      delete food.local.hashSource;
      await this.saveUserFoodAsync(food, 'cached');
    } catch (err) {
      this.reportUnpublishedFoodFailure('UnpublishedFoodPublishFailed', food._id, err);
    }
  }

  /**
   * The split `tryPublishUnpublishedFoodsAsync` makes, for the parts of the sync that run after it
   * returned: if the data is not reachable right now (offline, the server unreachable after its
   * retries, the database closing, the session refused), the next app start retries, so it is
   * only logged; anything else is reported.
   */
  private reportUnpublishedFoodFailure(name: string, foodId: string, err: unknown) {
    if (isBackgroundReadNotReady(err)) {
      console.log(`Syncing unpublished foods: ${foodId} not reachable right now, will retry next app start:`, err);
      return;
    }
    this.logging.trackSilentProblem(name, { foodId, ...errorReportFields(err) });
  }

  /**
   * Run the #3255 repair over the user's shared recipes. The rules are on
   * `repairSharedRecipesAsync`. Problems it meets are reported once per device, because this
   * runs on every start and most of them (a deleted ingredient, one the user does not own)
   * can never be repaired.
   */
  private async repairSharedRecipesAsync(sharedRecipes: Recipe[]) {
    const reportOnce = reportOncePerDevice(localStorage, (name, context) => this.logging.trackSilentProblem(name, context));
    await repairSharedRecipesAsync(sharedRecipes, {
      publishMissingIngredientsAsync: (recipe) => this.publishMissingRecipeIngredientsAsync(recipe, reportOnce),
      isTransientFailure: (err) => isDatabaseUnavailable(err) || err instanceof FoodReadFailedError || isNetworkError(err),
      reportProblem: reportOnce,
    });
  }

  /**
   * Publish every ingredient of this shared recipe that the shared food database lacks,
   * sub-recipes included, and answer how many could not be (#3255). The rules are on
   * `publishMissingRecipeIngredientsAsync`. A read that could not complete, or a publish
   * that could not reach the server, throws. `reportProblem` replaces the Sentry report
   * when the caller needs a different one.
   */
  async publishMissingRecipeIngredientsAsync(
    recipe: Recipe,
    reportProblem: (name: string, context: object) => void = (name, context) => this.logging.trackSilentProblem(name, context),
  ): Promise<number> {
    return publishMissingRecipeIngredientsAsync(recipe, {
      uid: this.auth.user?.uid,
      getPublishedIdsAsync: async (ids) => new Set((await this.getFoodHeadersByIdsAsync(ids)).filter(h => h).map(h => h._id)),
      readFoodAsync: async (id) => (await this.getFoodByIdAsync(id, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.Throw })) ?? undefined,
      publishAsync: (food) => this.cloudFunctionsService.publishFoodAsync({ food, adminMode: false }),
      isNetworkError,
      reportProblem,
    });
  }

  duplicateFood(food: Food) {
    let newFood = deepClone(food);
    // deepClone copies the underscore fields too, so the copy arrives carrying the
    // source document's _rev — a revision marker claiming this brand-new id already
    // exists at that version. That was harmless only while duplicates were never
    // saved (#5086), and wrong the moment one is.
    delete newFood._rev;
    newFood.status = FoodStatus.Private;
    newFood.EANs = undefined;
    newFood.name += ' (copy)';
    newFood.local = {};
    const foodTypeId = isRecipe(food) ? 'UDR' : 'UDF';
    generateUserFoodId(this.auth.user.uid, newFood, foodTypeId, crypto);
    return newFood;
  }

  createNewFood(name: string, ean: string): Food {
    if (ean !== undefined && typeof ean !== 'string') {
      this.logging.trackAbnormalError(`createNewFood(${name}, ${ean}) - EAN is not a string!`);
      ean = (ean as number).toString();
    }
    let food = {
      _id: null, // will be filled below
      image: {} as ScaledImage,
      name: name || '',
      EANs: ean ? [ean] : undefined,
      nutrients: {},
      measures: [],
      status: FoodStatus.Private,
    };
    generateUserFoodId(this.auth.user.uid, food, 'UDF', crypto);
    return food;
  }

  createNewRecipe(): Recipe {
    let recipe: Recipe = {
      _id: null, // will be filled below
      image: {} as ScaledImage,
      name: '',
      nutrients: {},
      ingredients: [],
      instructions: '',
      status: FoodStatus.Private,
    };
    generateUserFoodId(this.auth.user.uid, recipe, 'UDR', crypto);
    return recipe;
  }

  /**
   * Read either a food doc or a logged meal by id, whichever the id names.
   *
   * `opts.onReadFailure` governs the FOOD arm only — it is forwarded to
   * `getFoodByIdAsync` and means exactly what it means there. The log-meal arm has
   * its own separate rule: `getOneLogEntryAsync` answers `undefined` when the backlog
   * cache cannot serve that id, and no value here changes that.
   */
  async getFoodOrLogMealByIdAsync(
    foodId: string,
    opts: { cacheFood: boolean; onReadFailure: FoodReadFailurePolicy },
    watcher: DocWatcher,
  ): Promise<AnyFood> {
    if (isLogMealById(foodId)) {
      return this.userDb.getOneLogEntryAsync<Log_Meal>(foodId, watcher);
    } else {
      return this.getFoodByIdAsync(foodId, opts);
    }
  }

  /**
   * Read a food doc by id.
   *
   * Returns `null` when the food genuinely does not exist (a 404 / not-found).
   *
   * A transient read failure — a network blip, an iOS "Load failed", or the local
   * DB shutting down — is a different thing, and `opts.onReadFailure` says what to
   * do about it. `FoodReadFailurePolicy.Throw` raises a `FoodReadFailedError` for
   * those cases, so the caller can tell "could not read" apart from "does not
   * exist" and avoid mislabeling a blip as a missing food (Sentry Group 5);
   * `FoodReadFailurePolicy.TreatAsMissing` answers `null` for them, the
   * long-standing behavior.
   *
   * There is deliberately no default, and the option cannot be left out. Both
   * answers are legitimate depending on the caller, so no value is safe to assume —
   * see `FoodReadFailurePolicy` for why the build asks rather than a comment.
   */
  async getFoodByIdAsync(id: string, opts: { cacheFood: boolean; onReadFailure: FoodReadFailurePolicy }): Promise<Food> {
    if (!(typeof id === 'string' && id.startsWith('food/'))) {
      this.logging.trackAbnormalError(`getFoodByIdAsync(${id}) - id is not a string or does not start with food/`);
    }
    const cachedFood = await this.userDb.getUsedFoodByIdAsync(id);

    // A CURATED STAPLE COMES FROM THE DEVICE'S OWN COPY, ahead of both the user's cached copy and
    // the server (food staples plan 4c, #5625). A food is copied into the user's database the first
    // time they use it, and at the time this branch was written nothing ever refreshed that copy,
    // so a corrected staple never reached anybody who had already eaten it. Other official foods
    // are refreshed now (see refreshCachedFoodsIfStaleAsync below), but a staple still takes this
    // branch: the device's own snapshot is fresher than a read of the server would be. The id is
    // tested first so reading any other food does not wait on the staple snapshot at all — this
    // method runs for every ingredient of every meal opened.
    if (isStapleId(id)) {
      const staple = preferredStapleRead(id, await this.stapleDb.getStapleByIdAsync(id), cachedFood);
      if (staple) {
        normalizeFoodFormat(staple);
        staple.status = this.getFoodStatus(staple._id);
        // `opts.cacheFood` is deliberately ignored here; `stapleWithUserHistory` says why
        return staple;
      }
      // this device has not got the staple yet (it has not downloaded them, or this is a cold
      // offline boot) — fall through and read exactly as before
    }

    let fixOldFoodFormats = async (food: Food) => {
      // Fix food id from old meal/+UDM notation to new food/+UDM format
      if (id.startsWith('meal/')) {
        let matchResult = id.match(/^meal\/\+UDM-(.+)$/);
        if (matchResult) {
          let migratedFood = deepClone(food);
          migratedFood._id = `food/+UDM-${matchResult[1]}`;
          migratedFood._rev = undefined;
          migratedFood = await this.userDb.cacheFoodAsync(migratedFood);
          // nobody awaits this delete, so a rejection here would become an unhandled
          // rejection. Since df-6ce1f9d6830e the delete THROWS when the database is being
          // torn down rather than returning as though it had removed the old document, and
          // that is our own shutdown: the migrated copy is already written, the stale one is
          // simply left behind, and the next read migrates it again. Anything else is a real
          // failure and is reported.
          void this.userDb.deleteFoodAsync(food, DatabaseWrapper.getTransactionId()).catch(err => {
            if (isDatabaseTornDown(err)) return;
            this.logging.trackSilentProblem('food-db: deleting the pre-migration copy failed', {
              docId: food._id,
              error: describeErrorForLog(err),
            });
          });
          return migratedFood;
        }
      }
      return food;
    };

    if (cachedFood) {
      if (!cachedFood.status) {
        cachedFood.status = this.getFoodStatus(cachedFood._id);
      }

      // the first time this food is opened in this app start, ask the server whether it has
      // changed — this is the read that used to return a copy nothing ever refreshed
      return fixOldFoodFormats(await this.refreshCachedFoodIfStaleAsync(cachedFood));
    }

    await this.awaitGlobalFoodDbAsync();
    return this.database.withDocWatcherAsync(watcher => this.readFoodFromDatabaseAsync(id, opts, watcher, fixOldFoodFormats));
  }

  /** Whether the app is in the background and whether it believes it is online, right now. */
  private readConnectionMoment(): FoodReadConnectionMoment {
    return { documentHidden: document.hidden, online: this.onlineState.isLikelyOnline() };
  }

  /**
   * The database half of `getFoodByIdAsync`: read the food, retry once on a transient
   * failure, and answer per `opts.onReadFailure`. The caller owns the watcher.
   */
  private async readFoodFromDatabaseAsync(
    id: string,
    opts: { cacheFood: boolean; onReadFailure: FoodReadFailurePolicy },
    watcher: DocWatcher,
    fixOldFoodFormats: (food: Food) => Promise<Food>,
  ): Promise<Food> {
    try {
      let food = await this.database.getRecordAsync(id, watcher);

      // normalize immediately after DB fetch
      if (food) {
        normalizeFoodFormat(food);
        food.status = this.getFoodStatus(food._id);
      }

      // Cache food if not explicitly set to false
      if (opts.cacheFood) {
        food = await this.userDb.cacheFoodAsync(food);
      }

      return fixOldFoodFormats(food);
    } catch (error) {
      const outcome = classifyFoodReadError(error);
      // transient network error → retry once after a short delay
      if (outcome === FoodReadOutcome.Transient) {
        const firstFailure = this.readConnectionMoment();
        await sleep(2000);
        try {
          let food = await this.database.getRecordAsync(id, watcher);
          if (food) {
            normalizeFoodFormat(food);
            food.status = this.getFoodStatus(food._id);
          }
          if (opts.cacheFood) {
            food = await this.userDb.cacheFoodAsync(food);
          }
          return fixOldFoodFormats(food);
        } catch (retryError) {
          // if the doc was actually deleted between the two reads, the retry surfaces a
          // genuine not-found — that is "missing", not "could not read", so return null
          // (same as the NotFound branch below) rather than a read-failure.
          if (classifyFoodReadError(retryError) === FoodReadOutcome.NotFound) {
            // stable message + id in context so these group as one Sentry issue, not one per id
            this.logging.trackSilentProblem('getFoodByIdAsync not found', { id, error: retryError });
            return null;
          }
          // still failing after the retry: the read could not complete. Opted-in callers
          // get a typed error so they can keep the reference and self-heal; everyone else
          // keeps the historical null-on-failure contract.
          if (opts.onReadFailure === FoodReadFailurePolicy.Throw) {
            throw new FoodReadFailedError(id, retryError);
          }
          // if the user signed out while this read was in flight, the failure is expected
          // teardown, not a network problem worth a Sentry issue: the fetch layer refuses to
          // refresh a session nobody is using any more and throws an AbortError ("User signed
          // out during fetch"), which classifies as transient, so both the first read and the
          // retry above fail this way. Same reasoning as the DbUnavailable branch below.
          // This is the "Sign-out race convention" in design-docs/warm-resume.md.
          if (this.auth.isLikelySignOutRace()) {
            console.log(`getFoodByIdAsync: user signed out during read, skipping ${id}`);
            return null;
          }
          // a read that failed while the app was in the background or offline is the
          // ordinary way this app loses its connection, so it is recorded, not filed as a
          // problem (df-a4572fc2f544). Foreground and online at both reads stays a problem.
          const retryFailure = this.readConnectionMoment();
          const context = { id, error: retryError, firstFailure, retryFailure };
          if (chooseFoodReadFailureReport(firstFailure, retryFailure) === FoodReadFailureReport.TransientInfo) {
            this.logging.trackSilentInfo(FOOD_READ_NETWORK_ERROR_TRANSIENT, context);
          } else {
            this.logging.trackSilentProblem(FOOD_READ_NETWORK_ERROR_PROBLEM, context);
          }
          return null;
        }
      }
      if (outcome === FoodReadOutcome.NotFound) {
        // genuinely not found (404, e.g. a deleted recipe still referenced by an
        // ingredient) — the real "food does not exist" signal. Return null even for
        // opted-in callers; this is the ONLY null an opted-in caller ever sees.
        this.logging.trackSilentProblem('getFoodByIdAsync not found', { id, error });
        return null;
      }
      if (outcome === FoodReadOutcome.DbUnavailable) {
        // expected during logout/shutdown — background food caching may still be in-flight.
        // a "could not read" condition, not "missing", so opted-in callers get the typed
        // error and self-heal once the DB is back.
        if (opts.onReadFailure === FoodReadFailurePolicy.Throw) {
          throw new FoodReadFailedError(id, error);
        }
        console.log(`getFoodByIdAsync: database shutting down, skipping ${id}`);
        return null;
      }
      // unexpected read error — surface it for telemetry. still "could not read", not
      // "missing", so opted-in callers get the typed error too (after the report). this
      // keeps the contract crisp: for an opted-in caller, a null return means ONLY a
      // genuine not-found, never a failed read.
      this.logging.trackAbnormalException(error);
      if (opts.onReadFailure === FoodReadFailurePolicy.Throw) {
        throw new FoodReadFailedError(id, error);
      }
      return null;
    }
  }

  /**
   * Batch fetch foods from global database by IDs.
   * Does NOT cache foods to user DB - use for read-only operations like getting measures.
   * @returns Map of food ID to Food object (missing foods are omitted)
   */
  async getFoodsByIdsFromGlobalDbAsync(ids: string[]): Promise<Map<string, Food>> {
    try {
      return await this.readFoodsByIdsFromGlobalDbAsync(ids);
    } catch (error) {
      this.logging.trackAbnormalException(error);
      return new Map();
    }
  }

  /**
   * The same read as `getFoodsByIdsFromGlobalDbAsync`, but a failed read throws instead of being
   * reported here and answered with an empty map, so the caller can tell "not there" from "could
   * not look" and report it once, in its own words (food staples plan 4b).
   */
  async readFoodsByIdsFromGlobalDbAsync(ids: string[]): Promise<Map<string, Food>> {
    if (ids.length === 0) {
      return new Map();
    }

    await this.database.whenGlobalFoodDbAvailable;

    return this.database.withDocWatcherAsync(async watcher => {
      const foods = await this.database.getByIdListAsync<Food>(ids, watcher);
      const result = new Map<string, Food>();

      for (const food of foods) {
        if (food) {
          food.status = this.getFoodStatus(food._id);
          normalizeFoodFormat(food);
          result.set(food._id, food);
        }
      }

      return result;
    });
  }

  /**
   * Replace the user's own copies of these foods with the server's version, where the server has
   * something different, and remember that they were asked about.
   *
   * This is what makes a correction on the server — a fixed nutrient, or the pictures the official
   * recipes were given — reach somebody who had already used that food. Before it, a food was
   * copied into the user's database the first time they used it and never refreshed, so the server
   * copy was read by nobody who had one.
   *
   * It answers with the foods it actually replaced, so a caller holding its own row for a food can
   * redraw it. Every other food is left exactly as it was.
   *
   * **It never throws.** Callers fire it in the background, and one of them is the read that
   * answers "open this food" — a path that could not fail before this existed and must not start
   * failing now. Everything that can go wrong is caught here and reported here.
   *
   * **Offline.** The shared food database is read straight from the server, so a device with no
   * connection cannot answer. That read does not simply fail, though: it first waits for this
   * service to be started and then for the database to be attached, and those waits can take a
   * long time or never finish, so the whole read is given
   * `FOOD_FRESHNESS_READ_LIMIT_MS` and then given up on. Either way the user keeps the copy they
   * have, which is exactly what they saw before this existed, and further checks pause for a
   * minute so a spell offline costs one read rather than one per card on every screen.
   */
  async refreshCachedFoodsIfStaleAsync(cachedFoods: Food[]): Promise<Map<string, Food>> {
    const refreshed = new Map<string, Food>();
    const worthChecking = cachedFoods.filter(food => isFoodWorthCheckingForFreshness(food));
    const ids = this.freshnessLog.claimIdsToCheck(worthChecking.map(food => food._id), Date.now());
    if (ids.length === 0) {
      return refreshed;
    }

    try {
      const serverFoods = await this.readFoodsForFreshnessAsync(ids);
      // the read did not answer in time, or could not be made at all: the claims are given up so
      // these foods are asked about again, and nothing is recorded as checked
      if (!serverFoods) {
        this.freshnessLog.releaseClaim(ids);
        return refreshed;
      }
      const byId = new Map(worthChecking.map(food => [food._id, food]));
      for (const id of ids) {
        const cachedCopy = byId.get(id);
        const serverCopy = serverFoods.get(id);
        // a food the shared database does not have is not an error — a food can be withdrawn —
        // and there is then nothing to refresh it from
        if (!cachedCopy || !serverCopy) {
          continue;
        }
        // compare like with like: the server side was normalised as it was read, so normalise this
        // side too. Without it a copy written by an older version of the app differs on a detail
        // the user cannot see, and would be rewritten on the first run after every upgrade
        normalizeFoodFormat(cachedCopy);
        if (!cachedCopy.status) {
          cachedCopy.status = this.getFoodStatus(cachedCopy._id);
        }
        if (serverCopyWouldChangeCachedCopy(cachedCopy, serverCopy)) {
          // cacheFoodAsync assigns every key of what it is given onto the existing copy, so the
          // `local` block has to be taken off first. It is the one part of a food that is about
          // this user — their like mark, when they last used it, the measure they last chose —
          // and a shared food document must never carry one, so one that does is damaged
          if (serverCopy.local !== undefined) {
            this.logging.trackSilentProblem('SharedFoodCarriesLocalBlock', { foodId: id });
            delete serverCopy.local;
          }
          refreshed.set(id, await this.userDb.cacheFoodAsync(serverCopy));
        }
      }
      this.freshnessLog.markChecked(ids);
    } catch (err) {
      // a write that failed, or anything else unforeseen. The claims are given up rather than
      // recorded, so this is tried again; reported rather than swallowed, because a food that
      // silently stopped being refreshed is invisible otherwise
      this.freshnessLog.releaseClaim(ids);
      this.logging.trackSilentProblem('FoodFreshnessRefreshFailed', {
        foodCount: ids.length,
        error: describeError(err),
      });
    }
    return refreshed;
  }

  /**
   * The shared database's copies of these foods, or null when the read could not answer in time.
   *
   * The deadline is the point of this method. Two waits come before the read, and both are
   * promises that are only ever resolved — never rejected and never timed out — so before they
   * are met the read does not fail, it simply never finishes:
   *  - `whenInitialized`, because this service is started after navigation can already have
   *    opened a meal, and until then `this.database` does not exist at all (df-fd5e5dd18008:
   *    reading it anyway was a TypeError filed as a problem on every warm resume that hit it);
   *  - inside `readFoodsByIdsFromGlobalDbAsync`, the shared food database being attached.
   * See `FOOD_FRESHNESS_READ_LIMIT_MS` and `readWhenReadyWithinDeadlineAsync`.
   */
  private async readFoodsForFreshnessAsync(ids: string[]): Promise<Map<string, Food> | null> {
    try {
      const answer = await readWhenReadyWithinDeadlineAsync(
        this.whenInitialized,
        () => this.readFoodsByIdsFromGlobalDbAsync(ids),
        FOOD_FRESHNESS_READ_LIMIT_MS,
      );
      if (answer === FRESHNESS_READ_GAVE_UP) {
        // being offline is the ordinary reason and is not a problem to solve: the user keeps the
        // copy they have. Recorded at the quietest level so it stays visible without filing an
        // error for every offline session
        this.freshnessLog.pauseAfterFailure(Date.now(), FOOD_FRESHNESS_PAUSE_AFTER_FAILURE_MS);
        this.logging.trackSilentInfo('FoodFreshnessReadTimedOut', { foodCount: ids.length });
        return null;
      }
      return answer;
    } catch (err) {
      this.freshnessLog.pauseAfterFailure(Date.now(), FOOD_FRESHNESS_PAUSE_AFTER_FAILURE_MS);
      // being offline, a database that is closing, or CouchDB refusing the session while it is
      // being re-established (401/403 on resume) is expected in an offline-first app; anything
      // else is a condition somebody should look at
      if (isBackgroundReadNotReady(err)) {
        this.logging.trackSilentInfo('FoodFreshnessReadUnavailable', { foodCount: ids.length });
      } else {
        this.logging.trackSilentProblem('FoodFreshnessCheckFailed', {
          foodCount: ids.length,
          error: describeError(err),
        });
      }
      return null;
    }
  }

  /** The single-food form of `refreshCachedFoodsIfStaleAsync`; answers with whatever to show now. */
  async refreshCachedFoodIfStaleAsync(cachedFood: Food): Promise<Food> {
    const refreshed = await this.refreshCachedFoodsIfStaleAsync([cachedFood]);
    return refreshed.get(cachedFood._id) ?? cachedFood;
  }

  getFoodStatus(foodId: string, votes?: number) {
    if (foodId.startsWith('food/$')) {
      return FoodStatus.Official;
    }

    if (foodId.startsWith('meal/') || foodId.startsWith('food/+UDM')) {
      return FoodStatus.Private;
    }

    if (foodId.startsWith('food/+')) {
      /**
       * When voting is implemented add another check here that checks total number of votes
       * Foods with 0-5 votes are contributed
       * Foods with 5+ votes are Voted
       */
      return votes > 5 ? FoodStatus.Voted : FoodStatus.Contributed;
    }

    return FoodStatus.Unknown;
  }

  public setIngredientStatus(ingredient: Ingredient) {
    if (isIngredientReal(ingredient) && !ingredient.status) {
      ingredient.status = this.getFoodStatus(ingredient.foodId);
    }
  }

  async getFoodHeadersByIdsAsync(ids: string[]): Promise<FoodHeader[]> {
    let results: FoodHeader[] = [];

    let pendingToQuery: { [key: string]: number } = {};
    for (let i = 0; i < ids.length; i++) {
      let id = ids[i];
      console.assert(id.startsWith('food/'));
      let cached = this.foodHeaderCache.get(id);
      if (cached) {
        results[i] = cached;
      } else {
        pendingToQuery[id] = i;
      }
    }

    await this.awaitGlobalFoodDbAsync();
    let rows = await this.database.runQueryAsync('foodHeader', Object.keys(pendingToQuery));
    for (let row of rows) {
      let header: FoodHeader = {
        _id: row.id,
        name: row.value.name,
        recipe: row.value.recipe,
      };
      this.foodHeaderCache.set(row.id, header);
      results[pendingToQuery[row.id]] = header;
    }

    return results;
  }

  /**
   * Reads the user's own foods for the food-search page.
   *
   * With search text, every food is read and matched by name. With an EMPTY search box the
   * page is listing the foods logged before, which normally comes from the `userFoodsUsed`
   * view. That read is bounded and falls back to reading every food, because on iOS native it
   * could take an unbounded amount of time and left the page on a spinner forever (#4100).
   * See food-search-default-results.ts for how the two reads differ and why the substitute is
   * safe.
   *
   * Throws `DefaultFoodsUnavailableError` when the empty-search-box list could not be read
   * at all, so the page can say so instead of showing "you have no foods".
   */
  public async searchFoodByName_userAsync(searchText: string): Promise<FoodHeader[]> {
    // get also all cached from user's recent foods
    let userFoods: Food[];
    if (searchText.length > 0) {
      userFoods = await this.userDb.getAllFoodsAsync();
    } else {
      const result = await loadDefaultFoodsAsync({
        readUsedFoodsViewAsync: async () => {
          const rows = await this.userDb.database.runQueryAsync('userFoodsUsed', undefined, {
            startkey: '',
            endkey: '\uffff',
            include_docs: true
          });
          return rows.map(r => r.doc as Food);
        },
        readAllFoodsAsync: () => this.userDb.getAllFoodsAsync(),
        viewWaitMs: DEFAULT_FOODS_VIEW_WAIT_MS,
        fallbackWaitMs: DEFAULT_FOODS_FALLBACK_WAIT_MS,
        scheduleTimeout: (callback, ms) => setTimeout(callback, ms),
        clearScheduledTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
        nowMs: () => Date.now(),
        reportSelfHeal: (name, context) => this.logging.trackSilentInfo(name, context),
        reportProblem: (name, context) => this.logging.trackSilentProblem(name, context),
      });
      userFoods = result.foods;
    }

    // a food with no usable _id would make buildFoodHeader throw and take the whole list down
    // (#6350). leave it out of this search and report it, so whatever produced it can be found.
    const { kept, dropped } = splitRowsByUsableId(userFoods);
    if (dropped.length > 0) {
      this.logging.trackSilentProblem('FoodSearchRowWithoutId', {
        read: searchText.length > 0 ? 'allFoods' : 'defaultFoods',
        count: dropped.length,
        sample: describeRowWithoutId(dropped[0]),
      });
    }
    userFoods = kept;

    let foodHeaders: FoodHeader[] = userFoods.map(
      food =>
      ({
        ...buildFoodHeader(food, {
          formatNutrients: nutrients => FormatNutrients(nutrients),
          status: food.status || this.getFoodStatus(food._id),
          recipe: isRecipe(food),
        }),
        cachedFood: food,
        liked: food.local ? food.local.liked : undefined,
      })
    );

    return foodHeaders;
  }

  /**
   * The user's recent meals, newest first, older repeats dropped.
   *
   * `leaveOutMealId` names the meal the caller has open, so it never offers itself (#6188). It is
   * left out BEFORE repeats are collapsed: an earlier meal holding the same foods would otherwise
   * be dropped as a repeat of the open meal, and the slot would offer nothing at all.
   */
  public async searchRecentMealsByName_userAsync(searchText: string, leaveOutMealId?: string): Promise<FoodHeader[]> {
    void searchText;
    let recentMeals = await this.userDb.getRecentLogEntries<Log_Meal>(LogRecordType.Meal, this.debug.getNowLocal(), 14);
    if (leaveOutMealId) {
      recentMeals = recentMeals.filter(meal => meal._id !== leaveOutMealId);
    }

    // withDocWatcherAsync, not a hand-written create/destroy: generateMealTextAsync reads foods
    // and, when a meal's based-on food has gone or diverged from its recipe, writes as well —
    // and a read that could not happen throws DatabaseUnavailableError. A hand-written destroy
    // on the last line is never reached in that case, and the watcher then stays registered for
    // the rest of the session (df-8948ec16162f).
    let recentMealHeaders: FoodHeader[] = await this.userDb.database.withDocWatcherAsync(async watcher => {
      const headers: FoodHeader[] = [];

      for (let logMeal of recentMeals) {
        // skip empty meals
        if (!logMeal.ingredients || logMeal.ingredients.length === 0) {
          continue;
        }

        let timestamp = logMeal.time_utc;
        let time = DateTime.fromISO(timestamp).toLocal();
        // translate a generated "Meal 3" for display, so the name and the date beside it are in the same language
        let name = formatScheduledMealName(logMeal.name) + ', ' + formatRelativeDay(this.debug.getNowLocal(), time);
        let [description, displayNutrients] = await this.generateMealTextAsync(logMeal, watcher);

        headers.push({
          cachedFood: logMeal,
          _id: logMeal._id,
          name: name,
          description: description,
          displayNutrients: displayNutrients,
          status: FoodStatus.Recent,
          liked: undefined,
          recipe: false,
          meal: false,
          // the foods in the meal, so typing one of them finds it (#5246)
          altNames: recentMealSearchNames(logMeal, description),
        });
      }

      return headers;
    });

    // sort the meals by timestamp, most recent first
    recentMealHeaders.sort((a, b) => {
      return (a.cachedFood as Log_Meal).time_utc > (b.cachedFood as Log_Meal).time_utc ? -1 : 1;
    });
    // remove older repeats — the same foods logged again in the same meal slot
    recentMealHeaders = dropRepeatedRecentMeals(recentMealHeaders);

    // use the last 5 meals
    //this.recentMealHeaders = this.recentMealHeaders.slice(0, 5);

    return recentMealHeaders;
  }

  /**
   * What the search backend client borrows from this service. Built fresh for every search so
   * that the signed-in user and the auth-settled promise are read exactly as late as they were
   * when this code sat inline here. The client itself lives in food-search-elastic.ts, which is
   * where the 401 retry, the signed-out empty answer and the transient-failure rule are
   * explained and tested.
   */
  private elasticSearchDeps(): ElasticSearchDeps {
    return {
      authSettled: this.auth.whenFirebaseAuthSettled,
      authSettledWaitMs: AUTH_SETTLED_WAIT_MS,
      getCurrentUser: () => this.firebase.fbAuth.currentUser,
      searchTimeoutMs: SEARCH_REQUEST_TIMEOUT_MS,
      resolveApiOriginAsync: () => getApiOriginAsync(),
      // init carries the deadline's abort signal (#5320), honoured on web and ignored on native.
      // marked because the search and the suggestions decide themselves which failures to report
      // (a passing blip is not one), so the global fetch wrapper must not report them too (#6058)
      fetchImpl: (url, init) => fetch(url, markCallerReportsFailure(init)),
      formatNutrients: nutrients => FormatNutrients(nutrients),
      getFoodStatus: foodId => this.getFoodStatus(foodId),
      // Report it, but do NOT interrupt the user: every caller of this search already shows its
      // own translated message when the search fails — the Add Foods page shows the card above
      // its list or the "couldn't load foods" message below it, and both scanner pages show
      // their own failure alert. So a toast from here is the second message about one failure.
      //
      // It must be trackSilentProblem rather than trackAbnormalException, because
      // trackAbnormalException ALWAYS shows a toast when it reports: with a userMessage it shows
      // that text, and without one it shows the generic "we noticed something unusual, would you
      // like to report it?" prompt. Dropping the message alone would therefore not stop the
      // toast. This used to pass untranslatedString('Internal error in search'), which showed
      // English text to every language and called a search timeout an internal fault (df-cfd308fcb9a2).
      //
      // The search's own deadline is filed under a name of its own (#5320), so stalls can be
      // counted apart from real server errors — describeSearchFailureForReport picks the name.
      reportRealFailure: e => { const report = describeSearchFailureForReport(e); this.logging.trackSilentProblem(report.name, report.context); },
      // read when the deadline runs out, so a token stall on a phone already known to be offline
      // is shown and filed as a connection problem rather than a slow server (#6391)
      isLikelyOnline: () => this.onlineState.isLikelyOnline(),
      // run when the deadline runs out before any answer arrived, so a stall on a failing phone
      // network the app still believes is online is not filed as a slow server (df-bac7bf9dc8fa)
      checkConnectionAsync: () => this.onlineState.probeServerReachableAsync(SEARCH_STALL_CONNECTION_CHECK_MS),
    };
  }

  /** Searches the food backend by name. See food-search-elastic.ts. */
  public async searchFoodHeadersByName_elasticAsync(
    searchText: string, maxCount: number, typoTolerance: TypoTolerance = TypoTolerance.OneLetter,
  ): Promise<FoodHeader[]> {
    return searchFoodHeadersByNameAsync(searchText, maxCount, this.elasticSearchDeps(), typoTolerance);
  }

  /**
   * Asks the server's phrase index for whole search lines (#5410). Uses the same sign-in wait,
   * signed-in user and backend address as the full search. See food-suggest-client.ts.
   */
  public async requestFoodSuggestionsAsync(text: string, size: number, signal: AbortSignal): Promise<SuggestionRow[]> {
    const deps = this.elasticSearchDeps();
    return requestFoodSuggestionsAsync(text, size, signal, {
      authSettled: deps.authSettled,
      authSettledWaitMs: deps.authSettledWaitMs,
      getCurrentUser: deps.getCurrentUser,
      resolveApiOriginAsync: deps.resolveApiOriginAsync,
      fetchImpl: deps.fetchImpl,
    });
  }

  /** Searches the food backend by barcode. See food-search-elastic.ts. */
  public async searchFoodHeadersByEANs_elasticAsync(EANs: string[], maxCount: number): Promise<FoodHeader[]> {
    return searchFoodHeadersByEANsAsync(EANs, maxCount, this.elasticSearchDeps());
  }

  // wraps one query in a timeout, so if it takes too long we report a silent error and return undefined
  eanSearchWrapper<TResult>(name: string, func: (eans: string[]) => Promise<TResult | undefined>): (eans: string[]) => Promise<TResult | undefined> {
    return async (eans: string[]) => {
      try {
        let result = await timeout(15000, func(eans));
        return result;
      } catch (e) {
        this.logging.trackSilentProblem(e, `Error while querying ${name} for ${eans.join(', ')}`);
        // if the promise fails, we return undefined
        return undefined;
      }
    }
  };

  private searchFoodHeadersByEAN_userAsync = this.eanSearchWrapper('User', async (eans: string[]): Promise<FoodHeader[]> => {
    // get also all cached from user's recent foods
    let userFoodHeaders = await this.userDb.getUserFoodHeadersByEANsAsync(eans);
    return userFoodHeaders;
  });

  // Searches for foods by EAN barcode. Gets both user-specific and global foods that match the barcode,
  // and accounts for potential variations in EAN formats.
  // Returns `connectionError: true` when the global (elastic) lookup hit a transient
  // connectivity failure. We isolate that failure here (rather than letting it reject
  // the whole call) so the user's already-resolved local barcode matches still survive
  // a network blip, while the caller can still show an honest connection-error state
  // (#3743). Real (non-transient) errors still propagate.
  async searchFoodHeadersByEANAsync(ean: string): Promise<{ headers: FoodHeader[]; connectionError: boolean }> {

    let eanCombinations = generateAllGTINCombinations(ean);

    let connectionError = false;
    let [userFoodHeaders, globalFoodHeaders] = await Promise.all([
      this.searchFoodHeadersByEAN_userAsync(eanCombinations),
      this.searchFoodHeadersByEANs_elasticAsync(eanCombinations, 50)
        .catch(err => {
          // a transient blip must not discard the local matches resolving alongside
          if (err instanceof SearchConnectionError) {
            connectionError = true;
            return [] as FoodHeader[];
          }
          throw err;
        }),
    ]);

    return { headers: [...(userFoodHeaders ?? []), ...globalFoodHeaders], connectionError };
  }

  /** This function is just an alias for `saveUserFoodAsync()`.
   * Any saving of a food locally with automatically mark as used - but we don't want
   * to expose this externally.
   * This whole system could use some refactoring as this is a bit tangled up,
   * but it works for now, as it is.
   *
   * TODO: These should both probably be in UserDb, not in FoodDb!
   */
  async markFoodAsUsedAsync(food: Food) {
    return this.saveUserFoodAsync(food, 'used');
  }
  async markMealAsUsedAsync(mealLog: Log_Meal) {
    // mark the ingredients as used first...
    for (let ingredient of mealLog.ingredients) {
      if (ingredient.type !== IngredientType.Real) {
        continue;
      }
      let ingredientFood = await this.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      if (!ingredientFood) continue; // database shutting down
      await this.markFoodAsUsedAsync(ingredientFood);
    }
    // ...then mark the eventual main template/recipe last (so that it is sorted first when searching!)
    // only mark recipes as used (not Log_Meals, which are already in the user's log)
    if (mealLog.basedOnFoodId && isRecipeById(mealLog.basedOnFoodId)) {
      let basedOnFood = await this.getFoodByIdAsync(mealLog.basedOnFoodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      if (!basedOnFood) return; // database shutting down
      await this.markFoodAsUsedAsync(basedOnFood);
    }
  }

  // save user's custom food
  async saveUserFoodAsync(food: Food, markUsed: 'used' | 'cached') {
    food.local = food.local || {};
    if (markUsed === 'used') {
      food.local.lastUsed_utc = this.debug.getNowLocal().toUTC().toISO();
    }
    return this.userDb.cacheFoodAsync(food);
  }

  async getUsersSavedRecipesAsync(): Promise<Recipe[]> {
    return this.userDb.getAllSavedRecipesAsync();
  }

  async getUserFoodsAsync(): Promise<Food[]> {
    return this.userDb.getAllFoodsAsync();
  }

  async countCachedFoodsAsync(): Promise<number> {
    return (await this.userDb.getAllFoodsAsync()).length;
  }
  /** For any foods in userdb that exist in globaldb, remove them from localdb. (Used to test caching on demand.) */
  async uncacheAllFoodsAsync() {
    // clear the auto-plan cache key so caching will re-run
    localStorage.removeItem(AUTO_PLAN_FOODS_CACHED_KEY);

    const foods = await this.getUserFoodsAsync();
    const candidateFoods = foods.filter(food => food._id.startsWith('food/$'));

    if (candidateFoods.length === 0) {
      return;
    }

    // bulk fetch from global DB to check which foods exist there
    const candidateIds = candidateFoods.map(f => f._id);
    const existingGlobalFoods: any[] = await this.database.withDocWatcherAsync(
      watcher => this.database.getByIdListAsync(candidateIds, watcher));
    const existingIds = new Set(existingGlobalFoods.map(f => f._id));

    // only delete user foods that exist in the global DB
    const foodsToUncache = candidateFoods.filter(f => existingIds.has(f._id));

    if (foodsToUncache.length > 0) {
      // the delete can fall short without throwing — a row the recovery could not save
      // (df-c387b6a15d2d). A write refused because the database is shutting down throws
      // instead, and travels up to the caller (df-aa69b381d6d6). This
      // is a maintenance action somebody pressed, so a half-finished one that says
      // nothing leaves them believing the cache was cleared when it was not.
      const notUncached = await this.userDb.database.deleteRecordsAsync(foodsToUncache, DatabaseWrapper.getTransactionId());
      if (notUncached.length > 0) {
        this.logging.trackSilentProblem('uncache-all-foods left foods cached', {
          notUncachedCount: notUncached.length,
          requestedCount: foodsToUncache.length,
        });
      }
    }
  }

  async getMealNameSuggestionsAsync(watcher: DocWatcher): Promise<MealNameSuggestion[]> {
    // get all log entries in the vicinity of one week
    let defaultSuggestions: MealNameSuggestion[] = [
      { name: LL.meals.breakfast(), time_hr: 8 },
      { name: LL.meals.lunch(), time_hr: 12 },
      { name: LL.meals.dinner(), time_hr: 16 },
      { name: LL.meals.supper(), time_hr: 20 },
    ];

    let recentSuggestions: MealNameSuggestion[] = [];

    let allEntries = await this.userDb.getLogEntriesInRangeAsync(DateTime.utc().minus({ days: 7 }), DateTime.utc().plus({ days: 7 }), watcher);
    for (let log of allEntries) {
      if (log.type == LogRecordType.Meal) {
        recentSuggestions = recentSuggestions.filter(s => s.name !== log.name);
        recentSuggestions.push({
          // a user-typed meal name — never translated, so brand it at origin
          name: untranslatedString(log.name),
          time_hr: isoTimeToHours(DateTime.fromISO(log.time_utc).toLocal().toISOTime()),
        });
      }
    }

    let result;

    if (recentSuggestions.length >= 3) {
      result = recentSuggestions;
    } else {
      result = unionBy(recentSuggestions, defaultSuggestions, 'name');
    }
    result = sortBy(result, 'time_hr');
    return result;
  }

  /** timeLocal can be either number of hours (isoTimeToHours() e.g.), or actual timestamp. */
  async suggestMealNameAsync(day: DateTime, watcher: DocWatcher): Promise<MealNameSuggestion> {
    let suggestions = await this.getMealNameSuggestionsAsync(watcher);
    let now = this.debug.getNowLocal();

    // find all meals in that day
    let mealsInDay = (await this.userDb.getLogEntriesInRangeAsync(day.startOf('day').toUTC(), day.endOf('day').toUTC(), watcher)).filter(
      l => l.type === LogRecordType.Meal,
    );

    // find time of last already added meal in that day
    let lastMealTime_hr: number = 0;
    if (mealsInDay.length > 0) {
      let lastMeal = mealsInDay[mealsInDay.length - 1];
      lastMealTime_hr = isoTimeToHours(DateTime.fromISO(lastMeal.time_utc).toLocal().toISOTime());
    }

    return pickMealNameSuggestion(suggestions, {
      isToday: day.hasSame(now, 'day'),
      now_hr: isoTimeToHours(now.toISOTime()),
      takenNames: mealsInDay.map(m => m.name),
      lastMealTime_hr,
    });
  }

  /**
   * Get recent meals from the user's food log.
   * This is the shared source of truth for recent meal data.
   */
  async getRecentMealsAsync(daysBack: number, watcher: DocWatcher): Promise<Log_Meal[]> {
    const now = this.debug.getNowLocal();
    const startDate = now.minus({ days: daysBack });
    const endDate = now.endOf('day');

    const entries = await this.userDb.getLogEntriesInRangeAsync(startDate, endDate, watcher);
    const meals = entries.filter(e => e.type === LogRecordType.Meal) as Log_Meal[];

    // filter for non-empty meals (has calories)
    const nonEmptyMeals = meals.filter(m => m.nutrients[INFOODS.Calories] > 0);

    // deduplicate by time_utc + name, keeping the latest version
    const seen = new Map<string, Log_Meal>();
    for (const meal of nonEmptyMeals) {
      const key = `${meal.time_utc}|${meal.name}`;
      seen.set(key, meal);
    }

    return Array.from(seen.values());
  }

  /**
   * Get AnyFood objects from recent meals.
   * Log_Meal objects have ingredients and nutrients, so they can be used directly.
   * Deduplicates by ingredient fingerprint so the same meal logged on different days appears only once.
   */
  async getRecentMealFoodsAsync(daysBack: number, watcher: DocWatcher): Promise<AnyFood[]> {
    const recentMeals = await this.getRecentMealsAsync(daysBack, watcher);

    // deduplicate by ingredient fingerprint — keeps the most recent meal per unique ingredient set
    const seen = new Map<string, Log_Meal>();
    for (const meal of recentMeals) {
      const fp = this.getIngredientFingerprint(meal);
      // if we already saw this fingerprint, keep the newer meal (recentMeals is chronological)
      seen.set(fp, meal);
    }

    return Array.from(seen.values());
  }

  /**
   * Build a stable fingerprint from a food's ingredient IDs for deduplication.
   * Ignores amounts so that scaled versions of the same meal are treated as duplicates.
   */
  getIngredientFingerprint(food: ComplexFood): string {
    if (!food.ingredients?.length) return food._id || '';
    const parts = food.ingredients.map(ing => {
      if (isIngredientReal(ing)) return ing.foodId;
      return `macro:${ing.name}`;
    });
    return parts.sort().join('|');
  }

  /** Get all foods that are likely a user would want used in a meal solver. */
  async getMealFoodCandidatesAsync(watcher: DocWatcher, options?: MealCandidatesOptions): Promise<AnyFood[]> {
    const opts = {
      searchOnlyLocal: options?.searchOnlyLocal ?? false,
      includeRecentMeals: options?.includeRecentMeals ?? true,
      recentMealsDays: options?.recentMealsDays ?? 14,
    };

    let userFoods = await this.getUserFoodsAsync();
    let userRecipes = userFoods.filter(f => f.totalWeight_g > 0);
    let allCandidates: AnyFood[] = userRecipes;

    // add official recipes if not searching only local
    if (!opts.searchOnlyLocal) {
      await this.awaitGlobalFoodDbAsync();
      const officialRecipes: any[] = await this.database.withDocWatcherAsync(
        foodDbWatcher => this.database.getByIdHeadAsync('food/$OFR-', foodDbWatcher));
      allCandidates = unionBy(userRecipes, officialRecipes, '_id'); // unionBy guarantees that first array has precedence!
    }

    // add recent meal foods if enabled
    if (opts.includeRecentMeals) {
      const recentFoods = await this.getRecentMealFoodsAsync(opts.recentMealsDays, watcher);
      // recent foods have highest priority (user's actual eating patterns)
      allCandidates = unionBy(recentFoods, allCandidates, '_id');
    }

    let allowedCandidates = allCandidates.filter(f => !((f as Food).local && (f as Food).local.liked < 0)); // remove downvoted food from candidates!
    return allowedCandidates;
  }

  /** Vote a food up/down */
  voteFood(food: Food, direction: -1 | 1) {
    food.local = food.local || {};
    if (food.local && food.local.liked === direction) {
      delete food.local.liked;
    } else {
      food.local.liked = direction;
    }
    this.pubSub.publish(FoodLikedChangedEvent, { foodId: food._id, liked: food.local.liked ?? undefined });
  }

  // existence probe used by cacheAutoPlanFoodsAsync to validate the
  // AutoPlanFoodsCached localStorage flag against what user-db actually holds.
  // a flag set without recipes present means the cache state has drifted (see
  // signOutAsync in auth.service.ts and the iOS SQLite migration).
  private async hasOfficialRecipesCachedAsync(): Promise<boolean> {
    const heads = await this.userDb.database.withDocWatcherAsync(
      watcher => this.userDb.database.getByIdHeadAsync(AUTO_PLAN_FOODS_RECIPE_PREFIX, watcher, { limit: 1 }));
    return heads.length > 0;
  }

  /**
   * Reads the recipes that have to be copied into the user's database, together with the
   * foods they use. The watcher is released even when the read throws.
   */
  private async readAutoPlanCandidatesAsync(): Promise<ComplexFood[]> {
    return await this.userDb.database.withDocWatcherAsync(
      watcher => this.getMealFoodCandidatesAsync(watcher, { searchOnlyLocal: false, includeRecentMeals: false })) as ComplexFood[];
  }

  /** Copies the recipe library into the user's database. See food-auto-plan-cache.ts. */
  public async cacheAutoPlanFoodsAsync() {
    return this.autoPlanFoodsCache.cacheAsync();
  }

  async prepareTuningParamsFoodsAsync(tuningSources: MealTunerSource[]): Promise<MealTunerParams> {
    let params: MealTunerParams = {
      foods: {},
      meals: [],
      targets: {},
    };

    let foodCachingPromises = [];
    let foodPending = {};
    for (let sourceRecipe of tuningSources) {
      for (let ingredient of sourceRecipe.ingredients) {
        if (isIngredientReal(ingredient)) {
          let foodId = ingredient.foodId;
          if (!foodPending[foodId]) {
            let promise = this.getFoodByIdAsync(foodId, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
            foodCachingPromises.push(promise); // (TODO: can we not always cache here?!)
            foodPending[foodId] = true;
          }
        }
      }
    }

    let foodMap: { [foodId: string]: Food } = {};
    let foodCache: Food[] = await Promise.all(foodCachingPromises);
    for (let food of foodCache) {
      if (food && food._id) {
        foodMap[food._id] = food;
      }
    }

    for (let sourceRecipe of tuningSources) {
      try {
        let tunerMeal: { id: string; ingredients: MealTunerIngredient[] } = { id: sourceRecipe._id, ingredients: [] };

        for (let ingredient of sourceRecipe.ingredients) {
          let foodId = isIngredientReal(ingredient) ? ingredient.foodId : MACRO_INGREDIENT_FOOD_ID;
          let locked = isIngredientReal(ingredient) ? ingredient.locked : true;
          let food = foodMap[foodId];
          if (!isIngredientMacro(ingredient)) {
            if (!food) {
              console.log(`Can't find food ${foodId}!`);
              throw new Error('Invalid recipe/meal - ingredient is missing in database.');
            }

            let tunerFood = params.foods[foodId];
            if (!tunerFood) {
              if (isComplexFoodById(foodId)) {
                // clone nutrients to avoid mutating cached food data
                let nutrientsPer100g: Nutrients = {};
                for (let nutrientsKey in food.nutrients) {
                  nutrientsPer100g[nutrientsKey] = food.nutrients[nutrientsKey] / food.totalWeight_g * 100;
                }
                params.foods[foodId] = {
                  nutrientsPer_100g: nutrientsPer100g,
                };
              } else {
                params.foods[foodId] = {
                  nutrientsPer_100g: food.nutrients,
                };
              }
            }
          }

          let gramsPerMeasure = 1;
          let measures = ingredient.weight_g;
          let fractions = undefined;

          let custom_unit = isIngredientReal(ingredient) ? ingredient.custom_unit : undefined;
          if (custom_unit) {
            let foodMeasure: FoodMeasure = food.measures.find(m => m.name === custom_unit);
            if (foodMeasure) {
              gramsPerMeasure = foodMeasure.weight_g / foodMeasure.amount;
              measures = ingredient.weight_g / gramsPerMeasure;
              if (food.name.search(/egg/i) >= 0) {
                fractions = 1;
              } else {
                fractions = 2;
              }
            }
          }
          // a typed-in ingredient's numbers are stored per 100 g, and the tuner adds them as
          // given, so hand over what this ingredient's own weight actually contributes (#4427)
          let nutrients: Nutrients | undefined = undefined;
          if (isIngredientMacro(ingredient)) {
            const unusable = newUnusableNutrientInput();
            nutrients = macroIngredientNutrientsForWeight(ingredient, unusable);
            // a weight the helper refused means the tuner is being handed per-100 g numbers as
            // if they were absolute, which nothing else here could ever notice (df-10a57222aae3)
            reportUnusableNutrientInput(unusable, this.logging, 'meal-tuner.typed-in-ingredient', {
              mealId: sourceRecipe._id,
              ingredientName: ingredient.name,
            });
          }
          tunerMeal.ingredients.push({ foodId, gramsPerMeasure, measures, fractions, locked, nutrients });
        }
        params.meals.push(tunerMeal);
      } catch (err) {
        this.logging.trackAbnormalException(err);
      }
    }
    return params;
  }

  /**
   * Add up a meal's nutrition from its ingredients and write the result onto
   * `meal.nutrients`. Almost every caller saves the meal straight afterwards, so what this
   * writes is what the user's food log permanently claims.
   *
   * There are two ways an ingredient's food can fail to arrive, and they are deliberately
   * NOT treated as the same thing:
   *
   * - **The food is genuinely gone** (a 404 — say a recipe an ingredient still points at was
   *   deleted). Nothing can be added for it, so it contributes zero and the total below ends
   *   up short by whatever that ingredient held. That is the only thing possible here, but a
   *   saved total that is wrong must not be invisible, so it is reported.
   * - **The food could not be READ this time** (a network blip, an iOS "Load failed", or the
   *   local database shutting down or mid-recovery). The food is not missing, so it is not
   *   treated as missing: this method throws `FoodReadFailedError` and leaves `meal.nutrients`
   *   at its previous value instead of overwriting a correct total with a too-low one.
   *
   * **A caller must not save the meal when this throws.** The right response is to tell the
   * user it is a connection problem and let them try again — `showFoodLoadFailedAlertAsync`
   * is the shared message for it. Swallowing the error and saving anyway is the bug this
   * contract exists to prevent.
   */
  public async recalculateMealNutrientsAsync(meal: Log_Meal) {
    // read every real ingredient's food first (getFoodByIdAsync auto-fixes recipe totalWeight_g).
    // Throw so a read that FAILED arrives as an error rather than as null: without it a moment of
    // bad connectivity is indistinguishable from a deleted food, and gets written into the user's
    // log as a permanently too-low total.
    const foodIds = [...new Set(meal.ingredients.filter(isIngredientReal).map(ingredient => ingredient.foodId))];
    const foods = await Promise.all(foodIds.map(foodId => this.getFoodByIdAsync(foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw })));

    // only reached when every read succeeded. A read that FAILED rejects above, and that is the
    // point: it skips the assignment below, so meal.nutrients keeps its previous value rather than
    // being replaced by a total we already know is missing an ingredient. The error travels on to
    // the caller, whose job is not to save the meal. What the total contains -- which ingredients
    // count, how each scales, what is reported -- is decided in `sumSavedMealNutrients`, a plain
    // module the unit tests can run.
    const foodsById = new Map(foodIds.map((foodId, i) => [foodId, foods[i]]));
    meal.nutrients = sumSavedMealNutrients(meal, foodsById, this.logging);
  }

  /**
   * Get sub-ingredients from a complex food (recipe or meal).
   * Optionally auto-tunes ingredient weights to match target nutrients.
   * If existingIngredients provided, tunes all ingredients together (like old handleAutoTune flow).
   * Returns ingredients and basedOnFoodId for consistent tracking of source recipe/meal.
   */
  async getSubIngredientsAsync(
    food: ComplexFood,
    options?: {
      /** How many grams of this recipe/meal the user actually logged (#4427).
       * The ingredients come out of the recipe at the weights it was written down with, so a
       * caller that is splitting ONE logged entry must pass that entry's weight here or the
       * user gets the whole recipe back instead of their portion. Leave it out only when you
       * genuinely want the whole thing — filling an empty meal from a recipe does. */
      portionWeight_g?: number;
      targetsForTuning?: NutrientTargets;
      existingIngredients?: Ingredient[];  // existing meal ingredients to tune together with
      /** Where in `existingIngredients` the food being split sits, when it is one of them. That row
       * is about to be replaced by its parts, so it takes no part in the tuning at all
       * (df-69b7099c360e) -- which also keeps a locked recipe row from being named as the reason
       * nothing could be tuned (df-c0a73cdcaa46). Leave it out when the food being split is not
       * one of the meal's rows. */
      replacedIngredientIndex?: number;
    }
  ): Promise<{
    ingredients: Ingredient[];
    basedOnFoodId: string;
    tunedExistingIngredients?: Ingredient[];
    /**
     * Set when the tuner CRASHED rather than answering. The split still happened and the
     * ingredients are the untuned ones. Handed back rather than reported from here, so the
     * PAGE that asked for the tuning decides what its user is told -- this service has no way
     * of knowing which screen it is answering (df-fc1cd793ae3b).
     */
    tuningError?: string;
    /**
     * Set when the tuner ran correctly and found NO combination of these foods that fits the
     * targets. The ingredients are the untuned ones. Says which dead end it was, so the page can
     * tell its user what to change -- handed back for the same reason as `tuningError`
     * (df-c0a73cdcaa46).
     */
    tuningFoundNoSolution?: CannotTuneReason;
  }> {
    // which parts a split hands back is decided in one shared place, and it hands back
    // copies so the cached food is never touched. every ingredient comes back, including a
    // typed-in "macro" one -- see buildSplitIngredients for why that matters (#4427).
    const ingredients = buildSplitIngredients(food);

    // a real ingredient names a food that has to still be in the database. a typed-in one
    // carries its own numbers and names no food, so there is nothing to look up for it.
    for (const ingredient of ingredients) {
      if (isIngredientReal(ingredient)) {
        const subFood = await this.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
        if (!subFood) {
          console.log(`Can't find food ${ingredient.foodId}!`);
          throw new Error('Invalid recipe/meal - ingredient is missing in database.');
        }
      }
      this.setIngredientStatus(ingredient);
    }

    // a split that produced nothing is about to be handed to a caller that replaces the
    // logged entry with it, which would delete the user's food. the callers refuse to do
    // that, but nobody would ever find out why, so report it from the one place that knows
    // which food it was.
    if (ingredients.length === 0) {
      this.logging.trackSilentProblem('split-portion: the food had no parts to split into', {
        foodId: food._id,
        ingredientCount: food.ingredients?.length ?? 0,
      });
    }

    // cut the whole recipe down to the portion the caller logged (#4427). this runs before the
    // tuning below on purpose, so auto-tune also starts from the logged portion rather than
    // from the whole recipe.
    //
    // the test is whether the caller SUPPLIED the option, not whether its value is a number. a
    // caller splitting one logged entry always supplies it, even when that entry's weight turns
    // out to be missing — and that case must be reported, not quietly treated as "give me the
    // whole recipe". only a caller that wants the whole food (filling an empty meal from a
    // recipe) leaves the option out altogether. do NOT simplify this to a value check.
    if (options !== undefined && 'portionWeight_g' in options) {
      const scaling = scaleSplitIngredientsToPortion(food, ingredients, options.portionWeight_g);
      // if the amount could not be used, the user is about to be handed the WHOLE recipe in place
      // of their portion — that is the #4427 bug, so say so rather than letting it pass unnoticed.
      // NothingToScale is deliberately not reported here: an empty split hands nothing back, so
      // that harm cannot happen, and the no-parts report above already names the food
      // (df-6133b82caa3f).
      if (scaling === SplitPortionOutcome.CouldNotScale) {
        this.logging.trackSilentProblem('split-portion: could not scale to the logged portion', {
          foodId: food._id,
          portionWeight_g: options.portionWeight_g,
          wholeWeight_g: getComplexFoodTotalWeight(food),
          ingredientCount: food.ingredients?.length ?? 0,
          // each part's weight as recorded, so a 0 g weight can be told apart from a missing or
          // broken one. a value that is not a number is written out as text, because undefined
          // would vanish from the report and NaN would read as null.
          partWeights_g: ingredients.map(part => describeRecordedWeight(part.weight_g)),
        });
      }
    }

    // if no targets provided, return ingredients as-is
    if (!options?.targetsForTuning) {
      return { ingredients, basedOnFoodId: food._id };
    }

    // the meal's own rows, as deep copies so the originals are never touched, and WITHOUT the
    // row this split replaces -- that row is about to become its own parts, so tuning it
    // alongside them counted the recipe twice (df-69b7099c360e). The rule and the full
    // explanation live in mealRowsTunedWithSplitParts.
    const existingIngredients = mealRowsTunedWithSplitParts(options.existingIngredients, options.replacedIngredientIndex);

    // combine existing + sub-ingredients for tuning (like old handleAutoTune flow)
    const allIngredients = [...existingIngredients, ...ingredients];

    // auto-tune ingredient weights to match targets
    const tuningSource: MealTunerSource = {
      _id: food._id,
      ingredients: allIngredients,
    };
    const tuningParams = await this.prepareTuningParamsFoodsAsync([tuningSource]);
    tuningParams.targets = options.targetsForTuning;

    const tuner = new MealTuner();
    const results = tuner.solve(tuningParams);

    // the tuner CRASHED. it catches everything it throws and hands the message back here
    // instead, so an empty answer alone cannot tell a crash apart from the honest "no
    // combination of these foods fits" below. read the message and hand it to the caller, or
    // the only trace of our own arithmetic failing is an untuned split nobody can explain
    // (df-fc1cd793ae3b).
    //
    // asked through `outcome`, the same way `runAutoTuneAsync` does it in edit-meal.ts: a failed solve
    // carries no meal list at all, so reading the answer below without this check does not
    // compile. `solve` describes every value it catches, so the message carried up from here is
    // never empty.
    if (results.outcome === MealTunerOutcome.Failed) {
      return { ingredients, basedOnFoodId: food._id, tuningError: results.error };
    }

    // the tuner ran and found no combination that fits: return the original ingredients, and say
    // why, judged on every row that went into the tuning -- the meal's existing rows included,
    // because a locked or typed-in one among them is what the user can change (df-c0a73cdcaa46).
    // The row this split replaces is already absent, because it is kept out of the tuning
    // itself (df-69b7099c360e). Do NOT filter by `replacedIngredientIndex` a second time here:
    // the positions have shifted, so it would now drop a different, innocent row.
    if (results.meals.length === 0) {
      return { ingredients, basedOnFoodId: food._id, tuningFoundNoSolution: classifyCannotTune(allIngredients) };
    }

    // apply tuned weights to all ingredients
    const tunedMeal = results.meals[0];
    for (let i = 0; i < tunedMeal.ingredients.length; i++) {
      const tunedIngredient = tunedMeal.ingredients[i];
      const ingredient = allIngredients[i];
      if (isIngredientReal(ingredient)) {
        ingredient.weight_g = tunedIngredient.tunedMeasures * tunedIngredient.gramsPerMeasure;
      }
    }

    // return sub-ingredients and optionally tuned existing ingredients
    if (existingIngredients.length > 0) {
      return {
        ingredients,  // tuned sub-ingredients (from allIngredients after existingIngredients.length)
        basedOnFoodId: food._id,
        tunedExistingIngredients: existingIngredients,
      };
    }

    return { ingredients, basedOnFoodId: food._id };
  }

  /**
   * Write the meal tuner's answer back into a meal.
   *
   * This used to empty the meal and rebuild it from the answer alone, matching rows by food
   * id, which deleted the user's food three different ways (#4535) -- see
   * `rebuildMealIngredientsFromTuner`, which now does the rebuild and carries the full
   * explanation. That rebuild works by POSITION, so it needs the list the tuner was GIVEN.
   *
   * @param sourceIngredients The rows that were handed to the tuner. Pass this when the
   *   caller built the list itself and it is not simply the contents of `suggestion.id` --
   *   Auto-Tune on the meal page is that case, because it tunes the meal's own current rows.
   *   When it is omitted, the source is read back from `suggestion.id`, which is the id of
   *   the recipe or meal the suggestion was built from (`prepareTuningParamsFoodsAsync` sets
   *   it), and that is the right list for every "replace this meal with a suggestion" flow.
   */
  async fillMealFromTunerAsync(meal: Log_Meal, suggestion: MealTunerMeal, sourceIngredients?: Ingredient[]) {
    // guard against undefined suggestion from meal tuner
    if (!suggestion || !suggestion.ingredients) {
      return;
    }

    const source = sourceIngredients ?? await this.readTunerSourceIngredientsAsync(suggestion);

    // a source we could not read leaves nothing to rebuild against. fall back to the old
    // by-food-id rebuild rather than refusing the user's action, but say so -- silently
    // dropping their food is exactly what #4535 was.
    if (!source) {
      this.logging.trackSilentProblem('meal-tuner-fill: could not read the list the tuner was given', {
        mealId: meal._id,
        suggestionId: suggestion.id,
        tunedIngredientCount: suggestion.ingredients.length,
      });
      await this.fillMealFromTunerByFoodIdAsync(meal, suggestion);
      return;
    }

    const { ingredients, positionsMismatched } = rebuildMealIngredientsFromTuner(source, suggestion.ingredients);

    // the tuner answers with one row per row it was given, so a length disagreement means
    // this is not the list it was given, and position no longer identifies a row.
    if (positionsMismatched) {
      this.logging.trackSilentProblem('meal-tuner-fill: the tuner answer does not line up with its source', {
        mealId: meal._id,
        suggestionId: suggestion.id,
        sourceIngredientCount: source.length,
        tunedIngredientCount: suggestion.ingredients.length,
      });
      await this.fillMealFromTunerByFoodIdAsync(meal, suggestion);
      return;
    }

    // refresh each real row's cached display data from its food, the way the old rebuild did:
    // a row that came out of a suggested recipe should show that food's current name and unit.
    for (let i = 0; i < ingredients.length; i++) {
      const ingredient = ingredients[i];
      if (!isIngredientReal(ingredient)) {
        continue;
      }
      const tunedIngredient = suggestion.ingredients[i];
      const food = await this.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }); // (TODO: can we not always cache here?!)
      if (!food) {
        // the row still belongs in the meal -- removing it is the bug this method exists to
        // stop -- so keep the name it already carries, and report that it went unrefreshed.
        this.logging.trackSilentProblem('meal-tuner-fill: a tuned ingredient food could not be read', {
          mealId: meal._id,
          foodId: ingredient.foodId,
        });
        continue;
      }

      const measure =
        food.measures &&
        food.measures.length > 0 &&
        food.measures.find(m => Math.abs(m.weight_g / m.amount - tunedIngredient.gramsPerMeasure) < 0.01);

      ingredient.name = food.name;
      ingredient.custom_unit = measure ? measure.name : undefined;
      // setIngredientStatus only fills a status in when there is none, and a cloned row
      // carries the one its source was written down with. Clear it first so the row gets
      // this food status now, which is what the rebuild did before #4535.
      ingredient.status = undefined;
      this.setIngredientStatus(ingredient);
    }

    meal.ingredients = ingredients;
    if (suggestion.id) {
      meal.basedOnFoodId = suggestion.id;
    }
  }

  /**
   * Read back the rows the tuner was given, from the recipe or meal the suggestion names.
   * Returns null when that cannot be established, which the caller reports rather than
   * absorbing.
   */
  private async readTunerSourceIngredientsAsync(suggestion: MealTunerMeal): Promise<Ingredient[] | null> {
    if (!suggestion.id) {
      return null;
    }
    return this.userDb.database.withDocWatcherAsync(async watcher => {
      try {
        // this does by hand what getFoodOrLogMealByIdAsync does. What matters is the food branch
        // asking for Throw: without it a food read that FAILED comes back as null, which is
        // indistinguishable here from a food that does not exist -- and null sends the meal to the
        // lossy fallback below. A recipe-backed suggestion is the common case, so that is the
        // branch that most needed the distinction.
        //
        // Writing it out by hand used to be the ONLY way to get that, because the forwarder took
        // cacheFood and nothing else. It is not any more: the forwarder now takes onReadFailure too
        // and passes it straight through, so this could be one call to it. Collapsing it is a
        // behaviour-preserving tidy-up nobody has done yet, not a thing this shape still needs.
        const source: AnyFood = isLogMealById(suggestion.id)
          ? await this.userDb.getOneLogEntryAsync<Log_Meal>(suggestion.id, watcher)
          : await this.getFoodByIdAsync(suggestion.id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw });

        if (!source || !Array.isArray((source as ComplexFood).ingredients)) {
          return null;
        }
        return (source as ComplexFood).ingredients;
      } catch (err) {
        // a read that could not RUN is not the same as a suggestion that names nothing. Answering
        // null here would send the meal through the lossy by-food-id fallback and save it with the
        // user's food deleted -- over a momentary blip, which is the whole bug this method exists
        // to stop. Let the read failure travel up instead, leaving the meal exactly as it was.
        // Both classes mean the same thing: the log-meal read throws DatabaseUnavailableError, the
        // food read throws FoodReadFailedError, and a genuine not-found arrives as null, not here.
        if (isDatabaseUnavailable(err) || err instanceof FoodReadFailedError) {
          throw err;
        }
        this.logging.trackAbnormalException(err);
        return null;
      }
    });
  }

  /**
   * The pre-#4535 rebuild, kept ONLY as the fallback for when the list the tuner was given
   * cannot be established. It matches rows by food id, so it drops hand-typed rows and
   * collapses two helpings of one food into one -- which is why it is not the normal path.
   * Every use of it is reported by its caller.
   *
   * The new rows are built off to the side and written into the meal only once every food has
   * been read, so a read that throws leaves the meal exactly as the caller handed it over
   * (df-15ca8b14a549).
   */
  private async fillMealFromTunerByFoodIdAsync(meal: Log_Meal, suggestion: MealTunerMeal) {
    // the old ingredients are replaced, not merged -- but only at the end, see above
    const ingredients: Ingredient[] = [];

    for (let tunedIngredient of suggestion.ingredients) {
      let foodId = tunedIngredient.foodId;
      if (foodId === MACRO_INGREDIENT_FOOD_ID) {
        continue;
      }
      // ask for Throw: a read that could not run must refuse the whole rebuild, the same way a
      // failed source read does, rather than come back as null and be taken for a deleted food
      let food = await this.getFoodByIdAsync(foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw }); // (TODO: can we not always cache here?!)
      if (!food) {
        // the food really does not exist any more, so it cannot go into the meal. keep the rest
        // of the suggestion, and report the row that was left out.
        this.logging.trackSilentProblem('meal-tuner-fill: a tuned ingredient food does not exist', {
          mealId: meal._id,
          foodId,
        });
        continue;
      }

      let measure =
        food.measures &&
        food.measures.length > 0 &&
        food.measures.find(m => Math.abs(m.weight_g / m.amount - tunedIngredient.gramsPerMeasure) < 0.01);

      const currentIngredient = ingredients.find(ing => isIngredientReal(ing) && ing.foodId === foodId);
      if (currentIngredient) {
        currentIngredient.weight_g = tunedIngredient.tunedMeasures * tunedIngredient.gramsPerMeasure;
      } else {
        let ingredient: IngredientReal = {
          foodId: foodId,
          name: food.name,
          type: IngredientType.Real,
          weight_g: tunedIngredient.tunedMeasures * tunedIngredient.gramsPerMeasure,
          custom_unit: measure && measure.name,
          locked: tunedIngredient.locked,
        };
        this.setIngredientStatus(ingredient);
        ingredients.push(ingredient);
      }
    }
    meal.ingredients = ingredients;
    if (suggestion.id) {
      meal.basedOnFoodId = suggestion.id;
    }
  }

  fillMealFromOtherMeal(meal: Log_Meal, original: Log_Meal) {
    meal.ingredients = deepClone(original.ingredients);
    meal.basedOnFoodId = original.basedOnFoodId || original._id;
  }

  async generateMealTextAsync(entry: Log_Meal, watcher: DocWatcher): Promise<[string, UiDisplayNutrient[]]> {
    let nut = FormatNutrients(entry.nutrients, this.userDb, { targets: entry.targets });

    // If we're based on a food, (but not a recent meal!) then use its name
    if (entry.basedOnFoodId && !isLogMealById(entry.basedOnFoodId)) {
      // ask for Throw so a read that could not complete (network blip, iOS "Load failed",
      // local DB mid-recovery) is NOT mistaken for a deleted food: the null branch below
      // unlinks the meal from its recipe and SAVES that, so a mistaken null would delete
      // the link for good (df-30418130c5bc).
      let basedFood: AnyFood;
      try {
        basedFood = await this.getFoodOrLogMealByIdAsync(entry.basedOnFoodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw }, watcher); // it's ok to cache - the food is part of the meal
      } catch (err) {
        if (!(err instanceof FoodReadFailedError)) throw err;
        // if the source could not be read this time, draw the meal from its own foods and
        // store nothing. the link stays intact, so the recipe name comes back on the next
        // render whose read succeeds.
        // with Throw, getFoodByIdAsync raises this error BEFORE its own network-failure
        // report, so report here. skip the two conditions it deliberately leaves
        // unreported as expected teardown: a sign-out race and the local database
        // shutting down or recovering (same rule as edit-ingredient's meal total).
        if (!this.auth.isLikelySignOutRace() && !isDatabaseUnavailable(err.originalError)) {
          this.logging.trackSilentProblem('meal-based-food-read-failed', {
            message: err.message,
            docId: entry._id,
            foodId: err.foodId,
          });
        }
        return [getIngredientText(entry.ingredients, MEAL_DESCRIPTION_SEPARATOR), nut];
      }

      // A recipe-based meal should show the recipe's name only while its foods still
      // match that recipe. If the user replaced the meal's foods, the food set no
      // longer matches and the recipe name would be misleading (#4112). Treat that
      // "diverged" case exactly like a missing based-food: drop the stale link so the
      // card lists the actual foods instead. Amount-only changes still match.
      // isLogMeal is a type-narrowing guard, not a runtime redundancy: isRecipe takes a Food,
      // but basedFood is AnyFood (which includes Log_Meal), so we must exclude Log_Meal first to call it.
      const divergedFromRecipe =
        !!basedFood &&
        !isLogMeal(basedFood) &&
        isRecipe(basedFood) &&
        !ingredientFoodSetsMatch(entry.ingredients, basedFood.ingredients);

      if (basedFood && !divergedFromRecipe) {
        let text = basedFood.name;
        return [text, nut];
      } else {
        // This is fixup for meals that were based on foods that no longer exist, or
        // whose foods no longer match the based recipe.
        // We need to remove parameter from log entry, resave it, clear backlog and replace current instance in memory with fresh one to avoid potential conflicts
        // if current instance in memory is being modified further.
        entry.basedOnFoodId = undefined; // if the based food is gone or no longer matches, remove it
        await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), entry); // save the change
        await this.userDb.clearBacklogCacheAsync();
        const newStoredEntry = await this.userDb.getOneLogEntryAsync(entry._id, watcher);
        // if the re-read came back empty, keep the entry we already have.
        // getOneLogEntryAsync answers undefined when the backlog cache cannot serve
        // that id — after the clear above that means the regenerated range did not
        // hold it, or the regeneration was discarded as stale. Uncommon, not
        // routine, which is why it is reported rather than quietly skipped: the
        // in-memory entry now differs from what was just stored. Overwriting from
        // nothing used to empty this object, which is the identity map's one shared
        // copy of this meal — see design-docs/sync-integrity.md, "A record that
        // reaches the save path with no _id".
        if (newStoredEntry) {
          deepOverwrite(entry, newStoredEntry);
        } else {
          this.logging.trackSilentProblem('meal-based-food-fixup: re-read returned nothing', {
            docId: entry._id,
            type: entry.type,
            time_utc: entry.time_utc,
          });
        }
      }
    }

    // Otherwise, use the names of the meal foods
    return [getIngredientText(entry.ingredients, MEAL_DESCRIPTION_SEPARATOR), nut];
  }
}

